import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Clock3, Award, CircleCheck, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const CATEGORY_LABELS = {
  unternehmen: { de: "Unternehmen & Marke", en: "Company & Brand" },
  compliance: { de: "Compliance", en: "Compliance" },
  nettopolicen: { de: "Nettopolicen", en: "Net Policies" },
  immobilien: { de: "Immobilien / Relago", en: "Real Estate / Relago" },
  festgeld: { de: "Festgeldprozesse", en: "Fixed-Term Processes" },
  crm: { de: "CRM & GoHighLevel", en: "CRM & GoHighLevel" },
  beratung: { de: "Beratungsgespr\u00e4che", en: "Advisory Meetings" },
  leadmanagement: { de: "Leadmanagement", en: "Lead Management" },
  dokumentation: { de: "Dokumentation", en: "Documentation" },
  qualitaet: { de: "Qualit\u00e4tssicherung", en: "Quality Assurance" },
};

export default function Academy() {
  const { t, tt } = useLang();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/academy/courses"), api.get("/academy/progress")])
      .then(([c, p]) => {
        setCourses(c.data);
        setProgress(p.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = courses.filter((c) => {
    if (filter === "all") return true;
    if (filter === "mandatory") return c.is_mandatory;
    if (filter === "open") return !c.completed;
    if (filter === "completed") return c.completed;
    return true;
  });

  const continueCourse = courses.find((c) => !c.completed && c.completed_lessons > 0);

  return (
    <div className="space-y-6" data-testid="academy-dashboard">
      <div className="rounded-[20px] dark-section noise-overlay p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2.5">
              <GraduationCap className="h-6 w-6 text-[#D6C08D]" />
              <h1 className="font-display text-3xl font-semibold text-white">Eleven Academy</h1>
            </div>
            <p className="mt-2 text-sm text-white/60 max-w-lg">
              {t("Ihre strukturierte Ausbildung: Produktwissen, Compliance und Beratungsqualit\u00e4t \u2013 mit Zertifikaten.", "Your structured education: product knowledge, compliance and advisory quality \u2013 with certificates.")}
            </p>
          </div>
          {progress && (
            <div className="flex gap-6">
              <div className="text-center">
                <p className="font-mono-num text-3xl font-semibold text-white" data-testid="academy-progress-pct">{progress.progress_pct}%</p>
                <p className="text-[11px] text-white/50 uppercase tracking-wide">{t("Fortschritt", "Progress")}</p>
              </div>
              <div className="text-center">
                <p className="font-mono-num text-3xl font-semibold text-white">{progress.certificates}</p>
                <p className="text-[11px] text-white/50 uppercase tracking-wide">{t("Zertifikate", "Certificates")}</p>
              </div>
              <div className="text-center">
                <p className="font-mono-num text-3xl font-semibold text-[#D6C08D]">{progress.mandatory_open}</p>
                <p className="text-[11px] text-white/50 uppercase tracking-wide">{t("Pflicht offen", "Mandatory open")}</p>
              </div>
            </div>
          )}
        </div>
        {continueCourse && (
          <Link to={`/academy/kurs/${continueCourse.id}`} className="mt-6 flex items-center justify-between rounded-[14px] bg-white/8 border border-white/10 p-4 hover:bg-white/12 transition-colors" data-testid="academy-continue-card">
            <div className="min-w-0">
              <p className="text-xs text-white/50 uppercase tracking-wide">{t("Weiterlernen", "Continue learning")}</p>
              <p className="mt-1 text-sm font-medium text-white truncate">{tt(continueCourse.title)}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="w-28 hidden sm:block">
                <Progress value={continueCourse.progress_pct} className="h-1.5 bg-white/10" />
              </div>
              <ArrowRight className="h-4 w-4 text-[#D6C08D]" />
            </div>
          </Link>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="rounded-[10px]">
          <TabsTrigger value="all" className="rounded-[8px]" data-testid="academy-filter-all">{t("Alle", "All")}</TabsTrigger>
          <TabsTrigger value="mandatory" className="rounded-[8px]" data-testid="academy-filter-mandatory">{t("Pflicht", "Mandatory")}</TabsTrigger>
          <TabsTrigger value="open" className="rounded-[8px]" data-testid="academy-filter-open">{t("Offen", "Open")}</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-[8px]" data-testid="academy-filter-completed">{t("Abgeschlossen", "Completed")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-52 rounded-[16px]" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <Link key={c.id} to={`/academy/kurs/${c.id}`} data-testid={`course-card-${c.id}`}>
              <Card className="rounded-[16px] h-full card-hover">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="rounded-full text-[10px] font-normal">
                      {tt(CATEGORY_LABELS[c.category] || { de: c.category, en: c.category })}
                    </Badge>
                    {c.is_mandatory && (
                      <Badge variant="outline" className="rounded-full text-[10px] border-[#D6C08D]/50 text-[#A8905A]">{t("Pflicht", "Mandatory")}</Badge>
                    )}
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold leading-snug">{tt(c.title)}</h3>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-2 flex-1">{tt(c.description)}</p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> {c.duration_minutes} min</span>
                    <span>{c.total_lessons} {t("Lektionen", "lessons")}</span>
                    <span className="capitalize">{c.level}</span>
                  </div>
                  <div className="mt-4">
                    {c.completed ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                        <Award className="h-4 w-4" /> {t("Abgeschlossen \u2013 Zertifikat erhalten", "Completed \u2013 certificate issued")}
                      </span>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Progress value={c.progress_pct} className="h-1.5 flex-1" />
                        <span className="font-mono-num text-xs text-muted-foreground">{c.progress_pct}%</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
          {filtered.length === 0 && (
            <Card className="rounded-[16px] md:col-span-3">
              <CardContent className="py-16 text-center text-sm text-muted-foreground">{t("Keine Kurse in dieser Ansicht.", "No courses in this view.")}</CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
